import { useEffect, useState } from 'react'

/**
 * How old a lane observation is, in words an operator can act on.
 *
 * The card showed a status with no time on it. So a lane that failed two
 * seconds ago and a lane that failed before lunch looked identical, and the
 * only way to tell them apart was to press Test. When that came back green the
 * obvious conclusion was that the card had been lying, and a colour nobody
 * believes has stopped being an indicator.
 *
 * The number itself is not what matters. What matters is that "now" and "a
 * while ago" are visibly different states, so a red card is either a thing to
 * act on or a thing to refresh, and the operator can tell which without
 * pressing anything.
 *
 * Seconds, not a timestamp. "23:36:29Z" makes the reader do the subtraction,
 * at the moment they are least inclined to.
 */

/** Past this, the observation is old enough that its colour is a claim about
 *  history rather than about now. Twice the poll interval plus the cache, so a
 *  page that is refreshing normally never shows it. */
export const STALE_AFTER_SECONDS = 60

export interface ObservationAge {
  label: string
  stale: boolean
}

/**
 * A clock that ticks, for the pages that render an age.
 *
 * Without it the age is computed once, at render, and then stays where it was.
 * The settings page never re-polls, so a card sat at "11s ago" a full minute
 * later: the same undated status as before, now wearing a timestamp that makes
 * it look checked. That is worse than no timestamp, because an operator has no
 * reason to distrust a number.
 *
 * Ten seconds, matching the overview's poll, so the two never disagree by more
 * than one tick.
 */
export function useObservationClock(everyMs = 10_000): number {
  const [now, setNow] = useState(() => Date.now() / 1000)
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now() / 1000), everyMs)
    return () => clearInterval(timer)
  }, [everyMs])
  return now
}

export function observationAge(
  observedAt: number | null | undefined,
  now: number = Date.now() / 1000,
): ObservationAge | null {
  // Nothing rather than a guess. An absent observation time is the one case
  // where inventing "just now" would recreate the whole defect.
  if (!observedAt) return null

  const seconds = Math.max(0, Math.round(now - observedAt))
  const stale = seconds >= STALE_AFTER_SECONDS

  if (seconds < 5) return { label: 'just now', stale }
  if (seconds < 90) return { label: `${seconds}s ago`, stale }
  const minutes = Math.round(seconds / 60)
  if (minutes < 90) return { label: `${minutes} min ago`, stale }
  return { label: `${Math.round(minutes / 60)} h ago`, stale }
}
